import {
  SegmentData,
  StopBlock,
  TweenBlock,
  GroupBlock,
  OperatorRecord,
} from "../types";

export type NavigableStop = {
  type: "stop";
  stop: StopBlock;
  operator: OperatorRecord;
  markerNumber: number;
  segmentId: string;
  groupId?: string;
};

export type NavigableTween = {
  type: "tween";
  tween: TweenBlock;
  segmentId: string;
  groupId?: string;
};

export type NavigableDistance = {
  type: "distance";
  meters: number;
  seconds: number;
};

export type NavigableGroupStart = {
  type: "group-start";
  group: GroupBlock;
  segmentId: string;
};

export type NavigableItem =
  | NavigableStop
  | NavigableTween
  | NavigableDistance
  | NavigableGroupStart;

/**
 * Flatten all segments into a linear array of navigable items
 * with correct marker numbering. Mirrors the traversal order
 * used by TripperContent.tsx.
 */
export function flattenNavigableItems(
  segments: SegmentData[],
  operatorByEntityId: Record<string, NonNullable<OperatorRecord>>,
): NavigableItem[] {
  const items: NavigableItem[] = [];
  let markerNumber = 0;

  const visibleSegments = segments.filter(s => s.items.length > 0);

  for (const segment of visibleSegments) {
    for (let i = 0; i < segment.items.length; i++) {
      const block = segment.items[i];

      if (block.blockType === "GROUP") {
        const group = block as GroupBlock;
        items.push({ type: "group-start", group, segmentId: segment._id });

        for (let ci = 0; ci < group.items.length; ci++) {
          const child = group.items[ci];
          if (child.blockType === "STOP") {
            const stop = child as StopBlock;
            // Distance before grouped stop (except first in group)
            if (ci > 0 && ((stop.distanceFromPrev ?? 0) > 0 || (stop.timeFromPrev ?? 0) > 0)) {
              items.push({
                type: "distance",
                meters: stop.distanceFromPrev ?? 0,
                seconds: stop.timeFromPrev ?? 0,
              });
            }
            markerNumber += 1;
            const entityId = stop.entityRef?.entityId?.toString();
            items.push({
              type: "stop",
              stop,
              operator: entityId ? operatorByEntityId[entityId] ?? null : null,
              markerNumber,
              segmentId: segment._id,
              groupId: group._id,
            });
          } else {
            items.push({
              type: "tween",
              tween: child as TweenBlock,
              segmentId: segment._id,
              groupId: group._id,
            });
          }
        }
      } else if (block.blockType === "STOP") {
        const stop = block as StopBlock;
        markerNumber += 1;
        const entityId = stop.entityRef?.entityId?.toString();
        items.push({
          type: "stop",
          stop,
          operator: entityId ? operatorByEntityId[entityId] ?? null : null,
          markerNumber,
          segmentId: segment._id,
        });

        // Distance separator after a stop (if next block is a stop)
        const nextBlock = segment.items[i + 1];
        if (nextBlock?.blockType === "STOP") {
          const nextStop = nextBlock as StopBlock;
          if ((nextStop.distanceFromPrev ?? 0) > 0 || (nextStop.timeFromPrev ?? 0) > 0) {
            items.push({
              type: "distance",
              meters: nextStop.distanceFromPrev ?? 0,
              seconds: nextStop.timeFromPrev ?? 0,
            });
          }
        }
      } else {
        items.push({
          type: "tween",
          tween: block as TweenBlock,
          segmentId: segment._id,
        });
      }
    }
  }

  return items;
}
